import React from 'react';
import Footer from './Footer';
import weddingPointsOfInterest from './weddingLocations';
import './GeneralInfo.css';

const embeddedMapUrl = 'https://www.google.com/maps/d/embed?mid=1XQ3Icc89PNm6kZd4lExgJeYDPU6KChM';

const planningUpdates = [
    {
        date: 'Tuesday, July 21, 2026',
        title: 'A few updates',
        text: 'We have added some new questions and answers to the FAQ section. Key points: cell service in the area is lacking, but ticks can be numerous, especially if the weather is mild. Download maps ahead of time and check for ticks. No gifts needed, but we would love if you bring a copy of your favorite recipe!'
    },
    {
        date: 'Saturday, June 20, 2026',
        title: 'RSVP is available',
        text: 'Good news: official invitations were mailed yesterday, and invitees can RSVP here on the website. Just go to the RSVP tab and enter your name to start the process. Let us know if you encounter any issues!'
    },
    {
        date: 'Sunday, May 3, 2026',
        title: 'Weekend details are still coming together',
        text: 'Please note that some specifics are not confirmed. We will use this space for any changes to event times, transportation notes, RSVP timing, or other planning details guests should know before the weekend.'
    },
    {
        date: 'Sunday, May 3, 2026',
        title: 'Welcome',
        text: 'Welcome to the website! It was made with love by the bride. Please report any bugs or typos to your nearest wedding representative.'
    }
];

const funThingsToDo = [
    {
        name: 'Berkshire scenic drives',
        detail: 'See the winding roads, small towns, hills, and autumn views.',
        link: 'https://maps.google.com/?q=Berkshires+scenic+drive'
    },
    {
        name: 'Local hikes and outdoor spots',
        detail: 'The area has plenty of trails and overlooks if you want to enjoy some fresh air before the festivities.',
        link: 'https://maps.google.com/?q=hiking+near+New+Marlborough+MA'
    },
    {
        name: 'Longer hikes',
        detail: 'The Mohican Monument Trail and nearby Appalachian Trail sections are good choices if you want a more substantial outing.',
        link: 'https://maps.google.com/?q=hiking+near+Berkshires'
    },
    {
        name: 'Historic sites',
        detail: 'The Berkshires have historic New England homes, museums, and landmarks within driving distance.',
        link: 'https://maps.google.com/?q=historic+sites+near+New+Marlborough+MA'
    }
];

const faqItems = [
    {
        question: 'Are gifts expected? Is there a registry?',
        answer: 'We aren\'t expecting gifts, and we haven\'t made a registry because there\'s nothing in particular we need for our home together. The best gift you can give us is your presence if you\'re able to attend, or your thoughts and best wishes if you aren\'t.'
    },
    {
        question: 'What if I would still like to give a gift?',
        answer: 'If there is something you are excited to share with us, we\'re very greatful. Whether a favorite poem or book, a piece of art or music, or something entirely different, we\'d be delighted to keep that reminder of you in our home. '
    },
    {
        question: 'How about bringing my favorite recipe?',
        answer: 'If you have a favorite recipe, we would love it if you can bring a paper copy to the wedding. Both of us love good food and cook often.  We will collate these into a wedding cookbook and enjoy for years to come.'
    },
    {
        question: 'What should I wear?',
        answer: 'The wedding party will be formally attired. For guests we recommend formal dress- a suit or jacket and tie for men, a long dress for women. Practical footwear is recommended, as we plan to gather on the green between the ceremony and reception, weather permitting. Most importantly, we encourage guests to choose something in which they feel comfortable and at their best. September in the Berkshires is typically warm in the afternoons and cool in the evenings, so you may want to bring an outer layer.'
    },
    {
        question: 'Will transportation be provided?',
        answer: 'We are not planning to provide dedicated shuttles at this time. Because the venues and lodging are spread out in a rural area, we recommend having access to a car. We recommend groups coordinate to share transportation based on where they\'re staying. Please reach out if you would like help coordinating a ride with other guests.'
    },
    {
        question: 'Are there other things worth doing or seeing while I\'m here?',
        answer: 'Yes! The Berkshires are full of nature and the arts. Check out the map below for some starting points. We hope you will enjoy the area while you are in town.'
    },
    {
        question: 'Do I need to arrive on Friday?',
        answer: 'There are no formal scheduled events on Friday, so Saturday morning is fine for most guests. However, there will be a Friday morning hike for anyone who is already in the area and would like to join. There is also plenty to do and see nearby if you decide to make a longer weekend of it.'
    },
    {
        question: 'When will RSVP details be available?',
        answer: 'It\'s available now, until July 26th. Navigate to "RSVP" in the top navigation bar.'
    },
    {
        question: 'Anything else I should know?',
        answer: (
            <>
                Cell service in the area can be spotty, particularly around the even venues. We strongly recommend to download a map of the local area or otherwise prepare directions in advance.
                <br /><br />
                Additionally, be mindful of ticks in the area; the northeast is notorious for them. We advise to wear long pants for activities like hiking, and to check oneself for ticks after outdoor activites. Ticks are especially prevalent during mild weather, in wooded or open areas with long grass, but can appear even in recently cut grass or tufts alongside outdoor stairs. Make sure to check for ticks upon coming inside, and thoroughly before going to bed. <a href="https://www.mass.gov/info-details/personal-protection-against-ticks" target="_blank" rel="noopener noreferrer">Learn more about tick protection</a>.
            </>
        )
    }
];

function GeneralInfo() {
    return (
        <div className="general-info">
            <header className="page-header">
                <br /><br />
                <div className="page-header-image-layer" aria-hidden="true"></div>
                <h1>General Info</h1>
                <p></p><br />
            </header>

            <div className="botanical-wrapper">
                <div className="botanical-left"></div>
                <div className="botanical-right"></div>

                <section className="general-section updates-section">
                    <div className="section-header">
                        <h2 className="section-title">Planning Updates</h2>
                        <span className="section-subtitle">Check here for news</span>
                    </div>

                    <div className="updates-list">
                        {planningUpdates.slice(0, 3).map((item) => (
                            <article className="update-item" key={item.title}>
                                <span>{item.date}</span>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                        {planningUpdates.length > 3 && (
                            <details className="updates-older">
                                <summary>View earlier updates</summary>
                                {planningUpdates.slice(3).map((item) => (
                                    <article className="update-item" key={item.title}>
                                        <span>{item.date}</span>
                                        <h3>{item.title}</h3>
                                        <p>{item.text}</p>
                                    </article>
                                ))}
                            </details>
                        )}
                    </div>
                </section>

                <section className="general-section general-section-accent faq-section">
                    <div className="section-header">
                        <h2 className="section-title">Frequently Asked Questions</h2>
                        <span className="section-subtitle">Wedding Weekend Details</span>
                    </div>

                    <div className="faq-list">
                        {faqItems.map((item) => (
                            <details className="faq-item" key={item.question}>
                                <summary>{item.question}</summary>
                                <p>{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>

                <section className="general-section">
                    <div className="section-header">
                        <h2 className="section-title">Map and Local Spots</h2>
                        <span className="section-subtitle">Berkshire Guide</span>
                    </div>

                    <div className="map-frame">
                        <iframe
                            title="Wedding weekend Google My Maps map"
                            src={embeddedMapUrl}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </div>

                    <p className="map-note">
                        This custom Google Maps view includes the venues and nearby stops guests may want to reference during the weekend.
                        {' '}
                        <a href={embeddedMapUrl} target="_blank" rel="noopener noreferrer" className="poi-link">
                            Open full map
                        </a>
                    </p>

                    <div className="local-lists">
                        <section className="local-list" aria-labelledby="wedding-poi-heading">
                            <h3 id="wedding-poi-heading">Wedding Locations</h3>
                            <ul className="poi-list">
                                {weddingPointsOfInterest.map((item) => (
                                    <li className="poi-item" key={item.name}>
                                        <span>
                                            <a href={item.link} target="_blank" rel="noopener noreferrer" className="poi-name-link">
                                                {item.name}
                                            </a>
                                            {item.detail}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section className="local-list local-list-things" aria-labelledby="fun-things-heading">
                            <h3 id="fun-things-heading">Things To Do</h3>
                            <ul className="poi-list">
                                {funThingsToDo.map((item) => (
                                    <li className="poi-item" key={item.name}>
                                        <span>
                                            <a href={item.link} target="_blank" rel="noopener noreferrer" className="poi-name-link">
                                                {item.name}
                                            </a>
                                            {item.detail}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
}

export default GeneralInfo;
